"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { cn } from "./utils";
import { guarded, useDirtyDismiss } from "./dirty-guard";

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[1040] bg-black/50",
        className,
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  hideClose = false,
  dirty,
  onEscapeKeyDown,
  onInteractOutside,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  /** Omit the top-right close (X). Default false — existing usages are unchanged.
   *  GatedDialogContent sets this for required/forced flows (#1054). */
  hideClose?: boolean;
  /** #13715: the form holds unsaved changes. Escape, an outside click and the close (X)
   *  then ask "Discard changes?" first. Default false: no change. */
  dirty?: boolean;
}) {
  const { closeRef, ask } = useDirtyDismiss(dirty);
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      {/* #1049 — center + scroll the dialog inside a viewport-filling scroll container
          instead of translate-y-centering the fixed Content itself. A `fixed top-1/2
          -translate-y-1/2` element whose height approaches the viewport lands its TOP above
          the fold (measured top=-306px at a 677px viewport on the individual-weekly grow
          flow — the header, Update-Type selector, and ✕ close all became unreachable). A
          grid-centered scrollable overlay keeps a SHORT dialog visually centered (identical
          to before) AND guarantees a TALL one scrolls WITHIN the viewport so its top is
          always reachable (top never < 0) — regardless of any runtime transform/animation
          override that was defeating the translate centering. `p-4` gives edge breathing
          room; the Content keeps its own look, just positioned `relative` now. */}
      <div className="fixed inset-0 z-[1050] grid place-items-center overflow-y-auto p-4">
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 relative z-[1050] grid w-full max-w-[calc(100%-1rem)] gap-4 rounded-t-xl sm:rounded-lg border p-4 sm:p-6 shadow-lg duration-200 sm:max-w-md md:max-w-lg",
            className,
          )}
          onEscapeKeyDown={guarded(ask, onEscapeKeyDown)}
          onInteractOutside={guarded(ask, onInteractOutside)}
          {...props}
        >
          {children}
          {/* #13715: the dirty guard closes through this after "Discard". Rendered only for a
              caller that passes `dirty`, so every other usage keeps its DOM. */}
          {dirty !== undefined && (
            <DialogPrimitive.Close ref={closeRef} hidden tabIndex={-1} aria-hidden="true" />
          )}
          {!hideClose && (
            <DialogPrimitive.Close onClick={ask} className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4">
              <XIcon />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </div>
    </DialogPortal>
  );
}

/**
 * GatedDialogContent — a non-dismissable DialogContent for REQUIRED / forced flows
 * (#1054, EPIC #1052). Suppresses Escape + outside-interaction dismissal and omits the
 * close (X), so the dialog closes ONLY by completing the flow inside it (or an explicit
 * caller-provided exit). Reuses DialogContent, so it inherits the same layout / overlay
 * / viewport behavior — a SEPARATE variant, leaving every plain <DialogContent> usage
 * unchanged. General-purpose (onboarding tours, team requirements, gated actions), NOT
 * individual-updates-specific: the caller decides whether/when to render it, and gates
 * activation via a Cloudflare Flagship flag (default OFF).
 */
function GatedDialogContent({
  onEscapeKeyDown,
  onInteractOutside,
  onPointerDownOutside,
  ...props
}: React.ComponentProps<typeof DialogContent>) {
  return (
    <DialogContent
      hideClose
      onEscapeKeyDown={(event) => {
        event.preventDefault();
        onEscapeKeyDown?.(event);
      }}
      onInteractOutside={(event) => {
        event.preventDefault();
        onInteractOutside?.(event);
      }}
      onPointerDownOutside={(event) => {
        event.preventDefault();
        onPointerDownOutside?.(event);
      }}
      {...props}
    />
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  GatedDialogContent,
};
